import { environment } from 'src/environments/environment';

/**
 * The login session shared by every Fusion micro-frontend (this app, fusion-onboarding, ...).
 * Each app keeps its own localStorage copy of the session; this cookie — set on the parent domain
 * they all share (environment.sharedSession.cookieDomain) — is what tells every one of them who is
 * signed in. It only ever holds the auth token: everything else comes from the session endpoint.
 * Every logout must call clearSharedSession(), otherwise SharedSessionService signs the user
 * straight back in on the next page load. Same file as fusion-onboarding's
 * epics/onboarding/utils/shared-session-cookie.ts — keep them in sync.
 */
const { cookieName, cookieDomain, maxAgeDays } = environment.sharedSession;

const HANDOFF_HINTS_COOKIE = `${cookieName}_handoff`;

/** One-time flags the onboarding app sets for the next app (see consumeHandoffHints). */
export interface SessionHandoffHints {
    isNewOnboardedUser?: boolean;
    isViewOnlyMode?: boolean;
}

export function readSessionToken(): string | null {
    return readCookie(cookieName);
}

export function writeSessionToken(token: string): void {
    writeCookie(cookieName, token, maxAgeDays * 24 * 60 * 60);
}

/** Signs the browser out of every Fusion app. */
export function clearSharedSession(): void {
    writeCookie(cookieName, '', 0);
    writeCookie(HANDOFF_HINTS_COOKIE, '', 0);
}

/** Reads and deletes the onboarding app's one-time flags, if any. */
export function consumeHandoffHints(): SessionHandoffHints {
    const raw = readCookie(HANDOFF_HINTS_COOKIE);
    if (!raw) {
        return {};
    }
    writeCookie(HANDOFF_HINTS_COOKIE, '', 0);
    try {
        return JSON.parse(raw) as SessionHandoffHints;
    } catch {
        return {};
    }
}

export function readCookie(name: string): string | null {
    const prefix = `${name}=`;
    const match = document.cookie.split('; ').find((part) => part.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) || null : null;
}

export function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
    const attributes = [`${name}=${encodeURIComponent(value)}`, 'Path=/', `Max-Age=${maxAgeSeconds}`, 'SameSite=Lax'];
    if (cookieDomain) {
        attributes.push(`Domain=${cookieDomain}`);
    }
    if (location.protocol === 'https:') {
        attributes.push('Secure');
    }
    document.cookie = attributes.join('; ');
}
